import React, { memo, useCallback } from 'react'

import { BubbleMenu } from '@tiptap/react/menus'
import { HiOutlineChatBubbleLeftRight } from 'react-icons/hi2'
import {
  TbBold,
  TbCircleCheck,
  TbHighlightOff,
  TbItalic,
  TbStrikethrough,
  TbUnderline,
} from 'react-icons/tb'
import { v4 as uuidv4 } from 'uuid'

import {
  ContentTypeDropdown,
  ContentTypePopover,
} from '@/components/common/TextArea/RichTextArea/menus/components/ContentTypeDropdown'
import { EditLinkPopover } from '@/components/common/TextArea/RichTextArea/menus/components/EditLinkPopover'
import { LinkEditorPanel } from '@/components/common/TextArea/RichTextArea/menus/components/LinkEditorPanel'
import { MoreOptionsPopover } from '@/components/common/TextArea/RichTextArea/menus/components/MoreOptionsPopover'
import { Toolbar } from '@/components/common/TextArea/RichTextArea/menus/components/Toolbar'
import {
  useTextMenuCommands,
  useTextMenuContentTypes,
  useTextMenuDisabledState,
  useTextMenuState,
} from '@/components/common/TextArea/RichTextArea/menus/TextMenu/hooks'
import type { MenuProps } from '@/components/common/TextArea/RichTextArea/menus/types'
import type { AddCommentRequest } from '@/components/common/TextArea/RichTextArea/types'

import { UUID } from '@/types/Id'

const MemoButton = memo(Toolbar.Button)
const MemoContentTypePopover = memo(ContentTypePopover)

type OptionsProps = NonNullable<
  React.ComponentProps<typeof BubbleMenu>['options']
>

const TEXT_MENU_OPTIONS: OptionsProps = {
  placement: 'top-start' as const,
  offset: 8,
  flip: {
    fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'] as const,
  },
}

interface TextMenuProps extends MenuProps {
  onAddComment?: (request: AddCommentRequest) => void
}

export const TextMenu: React.FC<TextMenuProps> = ({
  editor,
  appendTo,
  onAddComment,
}) => {
  const commands = useTextMenuCommands({ editor })
  const states = useTextMenuState({ editor })
  const blockOptions = useTextMenuContentTypes({ editor })

  const handleAddComment = useCallback(() => {
    const request: AddCommentRequest = {
      optimisticId: uuidv4() as UUID,
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    }
    onAddComment?.(request)
  }, [editor, onAddComment])

  return (
    <BubbleMenu
      options={TEXT_MENU_OPTIONS}
      editor={editor}
      pluginKey="textMenu"
      shouldShow={states.shouldShow}
      updateDelay={100}
    >
      <Toolbar.Wrapper className="shadow-md">
        <MemoContentTypePopover options={blockOptions ?? []} />
        <Toolbar.Divider />
        <MemoButton
          tooltip="Bold"
          onClick={commands.onBold}
          isActive={states.isBold}
        >
          <TbBold className="w-4 h-4" />
        </MemoButton>
        <MemoButton
          tooltip="Italic"
          onClick={commands.onItalic}
          isActive={states.isItalic}
        >
          <TbItalic className="w-4 h-4" />
        </MemoButton>
        <MemoButton
          tooltip="Underline"
          onClick={commands.onUnderline}
          isActive={states.isUnderline}
        >
          <TbUnderline className="w-4 h-4" />
        </MemoButton>
        <MemoButton
          tooltip="Strikethrough"
          onClick={commands.onStrike}
          isActive={states.isStrike}
        >
          <TbStrikethrough className="w-4 h-4" />
        </MemoButton>
        <EditLinkPopover onSetLink={commands.onSetLink} />
        {states.canApprove && (
          <MemoButton
            tooltip="Approve"
            onClick={commands.onApproveComment}
            isActive={states.isHighlight}
          >
            <TbCircleCheck className="w-4 h-4" />
          </MemoButton>
        )}
        {onAddComment && !states.canApprove && (
          <MemoButton
            tooltip="Add comment"
            onClick={handleAddComment}
            disabled={editor.state.selection.empty}
          >
            <HiOutlineChatBubbleLeftRight className="w-4 h-4 stroke-[2px]" />
          </MemoButton>
        )}
        <MoreOptionsPopover editor={editor} />
      </Toolbar.Wrapper>
    </BubbleMenu>
  )
}

export {
  ContentTypeDropdown,
  ContentTypePopover,
  EditLinkPopover,
  LinkEditorPanel,
  MoreOptionsPopover,
  Toolbar,
  useTextMenuCommands,
  useTextMenuContentTypes,
  useTextMenuDisabledState,
  useTextMenuState,
}
